import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ConfigProvider, Select as AntdSelect } from 'antd'
import cls from 'classnames'
import styled from 'styled-components'

import Button from 'components/lib/DesignSystem/Button'
import Checkbox from 'components/lib/DesignSystem/Checkbox'
import Chip from 'components/lib/DesignSystem/Chip'
import { ChevronDown, Loader } from 'components/lib/DesignSystem/Icons'
import { ClearIcon } from 'components/lib/DesignSystem/Inputs/InputWrapperBase'
import { styledDropdownScrollbar } from 'components/styles/sharedStyledCSS'

import Icon from '../Icon'
import withValidation from '../inputs/withValidation'
import LabelText from '../LabelText'

import { extractReactNodeText, getPlaceholderTooltipItems } from './Select.helpers'
import {
  DesignSystemSelectPopupGlobalStyle,
  DS_SELECT_POPUP_CLASS,
  DS_SELECT_POPUP_MULTIPLE_CLASS,
  SelectStyleWrapper,
} from './SelectStyleWrapper'
import Tooltip from './Tooltip'

export const VARIANTS = {
  OUTLINE: 'outline',
  FILLED: 'filled',
}

/**
 * @typedef {Object} CustomTagProps
 * @property {number} tagMaxWidth - Maximum width for tag
 */

/**
 * Custom tag renderer component for Select
 * @param {CustomTagProps} props - The props object
 * @returns {Function} Tag render function
 */

const MaxTagPlaceholderMeta = ({ children }) => children

const CustomTag =
  ({ tagMaxWidth, tagsRenderStyle }) =>
  ({ label, onClose, className }) => {
    const isMaxTagPlaceholder = Boolean(label?.props?.isMaxTagPlaceholder)
    const isMultiTagPlaceholder =
      isMaxTagPlaceholder && label?.props?.tagsRenderStyle === 'multiple'
    const tooltipItems = isMultiTagPlaceholder ? label?.props?.tooltipItems : undefined
    const tooltipContent =
      isMultiTagPlaceholder && Array.isArray(tooltipItems) && tooltipItems.length ? (
        <div className="select-max-tag-tooltip-content">
          {tooltipItems.map(({ key, label: itemLabel }) => (
            <div key={key} className="select-max-tag-tooltip-item typography-subtitle-4">
              {itemLabel}
            </div>
          ))}
        </div>
      ) : undefined

    const labelText = extractReactNodeText(label) || 'Selected'

    return (
      <div className={className}>
        <Chip
          checked
          checkedStyle="selected"
          size="small"
          label={labelText}
          onClose={onClose}
          style={{ 'margin-inline-end': '6px' }}
          maxWidth={tagMaxWidth}
          bordered={tagsRenderStyle === 'multiple'}
          forceTooltip={Boolean(tooltipContent)}
          tooltipContent={tooltipContent}
          tooltipProps={
            tooltipContent
              ? {
                  placement: 'top',
                  overlayInnerStyle: {
                    padding: '12px 16px',
                    maxWidth: 260,
                    textAlign: 'left',
                  },
                  destroyTooltipOnHide: true,
                }
              : undefined
          }
        />
      </div>
    )
  }

/**
 * @typedef {Object} CustomMaxTagPlaceholderProps
 * @property {boolean} showShort - Whether to show shortened version
 * @property {string} tagsRenderStyle - Style of tag rendering ('single' or 'multiple')
 */

/**
 * Custom max tag placeholder renderer component for Select
 * @param {CustomMaxTagPlaceholderProps} props - The props object
 * @returns {Function} Max tag placeholder render function
 */
const CustomMaxTagPlaceholder =
  ({ showShort, tagsRenderStyle }) =>
  (items) => {
    // Ensure we're working with the actual count, not components
    const itemCount = Array.isArray(items) ? items.length : 0

    const labelText =
      tagsRenderStyle === 'single'
        ? `${itemCount}${!showShort ? ' selected' : ''}`
        : `${!showShort ? '+' : ''}${itemCount}`

    const shouldShowTooltip = tagsRenderStyle === 'multiple'
    const tooltipItems = shouldShowTooltip ? getPlaceholderTooltipItems(items) : undefined

    return (
      <MaxTagPlaceholderMeta
        isMaxTagPlaceholder
        label={labelText}
        tooltipItems={tooltipItems}
        tagsRenderStyle={tagsRenderStyle}
      >
        {labelText}
      </MaxTagPlaceholderMeta>
    )
  }

/**
 * @typedef {Object} LoadingProps
 * @property {string} className - CSS class name
 */

/**
 * Option content for Select dropdown items
 */
const SelectOptionContent = ({ option, multiple, value, labelAsValue, optionKey }) => {
  const checkboxId = `${optionKey}_Checkbox`
  const checkedValue = labelAsValue ? option.label : (option.value ?? option)
  const isChecked = Array.isArray(value) ? value?.includes(checkedValue) : false

  const optionLabelText = extractReactNodeText(option.label)
  return (
    <div className="option" title={optionLabelText || undefined}>
      {multiple ? <Checkbox id={checkboxId} preventDefault checked={isChecked} /> : null}
      {option.icon ? (
        <span className="left_icon">
          <Icon size="15px" imageName={option.icon} />
        </span>
      ) : null}
      {option.preLabel ? <span className="pre-label">{option.preLabel}</span> : null}
      <div className="option_label typography-subtitle-4">{option.label}</div>
    </div>
  )
}

/**
 * Dropdown render wrapper for Select - shows loader or default content with add-item button
 */
const SelectDropdownRender = ({
  defaultDropdown,
  searchLoader,
  hasMatchingOptions,
  addItemButtonComponent,
}) => (
  <>
    {searchLoader ? <Loading /> : defaultDropdown}
    {!searchLoader && hasMatchingOptions && addItemButtonComponent}
  </>
)

/**
 * Loading component for Select
 * @param {LoadingProps} props - The props object
 * @returns {JSX.Element} Loading component
 */
const Loading = ({ className }) => (
  <div className={cls('main-loader', className)} title="Loading...">
    <Loader id="Dropdown_Loading_icon" spin fill="var(--primary)" width="20px" height="20px" />
    <div id="Dropdown_LoadingSearch_text" className="main-loader__text">
      Loading...
    </div>
  </div>
)

/**
 * @typedef {object} SelectProps
 * @prop {string} [dropdownClassName] - Class name for dropdown
 * @prop {string} [error] - Error message
 * @prop {Object} [style] - Style object
 * @prop {string} [className] - Class name
 * @prop {boolean} [showSearch=true] - Whether to show search input
 * @prop {string} [placeholder] - Placeholder text
 * @prop {Function} [onChange=()=>{}] - Change handler, affected by selectionLimit
 * @prop {Array<Object>} [options=[]] - Options array
 * @prop {Array<Object>} [customOptions] - Custom options array
 * @prop {string|Array|Object|number} [value] - Selected value
 * @prop {boolean} [disabled=false] - Whether select is disabled
 * @prop {Function} [getPopupContainer] - Get popup container function
 * @prop {string} [id] - ID attribute
 * @prop {boolean|Function} [filterOption] - Custom filter behaviour for search results
 * @prop {string} [labelText] - Label text
 * @prop {Object} [popup] - Popup config object
 * @prop {boolean} [multiple=false] - Whether multiple selection is allowed
 * @prop {boolean} [readOnly=false] - Whether select is read only
 * @prop {boolean} [allowClear] - Whether to show clear button
 * @prop {boolean} [labelAsValue=false] - Whether to use label as value
 * @prop {string} [optionFilterProp='value'] - Option filter prop
 * @prop {string} [optionLabelProp='children'] - Option label prop
 * @prop {string} [notification] - Notification text
 * @prop {string} [name] - Name attribute
 * @prop {Function} [onSearch] - Search handler
 * @prop {Function} [onAddItem] - Add item handler
 * @prop {Function} [addItemValidation] - Add item validation function
 * @prop {boolean} [addItemLoader=false] - Whether add item is loading
 * @prop {string} [addItemError=''] - Add item error message
 * @prop {Function} [onLazyLoad] - Lazy load handler
 * @prop {boolean} [lazyLoader=false] - Whether lazy loading is active
 * @prop {boolean} [searchLoader=false] - Whether search is loading
 * @prop {string} [searchValue] - Search value
 * @prop {Function} [onClear] - Clear handler
 * @prop {Function} [onBlur] - Blur handler
 * @prop {string} [AddItemButtonText='Add to list'] - Add item button text
 * @prop {Function} [renderNewItemLabel] - Render new item label function
 * @prop {string} [AddItemInternalValidationErrorText='Item is not valid'] - Add item validation error text
 * @prop {('single'|'multiple')} [tagsRenderStyle='single'] - Tags render style
 * @prop {boolean} [backspaceTagRemove=true] - Whether backspace removes tags
 * @prop {Function} [onSelect] - Select handler, not affected by selectionLimit
 * @prop {number} [selectionLimit] - Maximum number of items that can be selected
 * @prop {Function} [onDropdownVisibleChange] - Dropdown visibility change handler
 * @prop {number} [maxTagCount] - Maximum number of tags to show
 * @prop {Object} [meta] - Meta object
 * @prop {number} [width] - Select width
 * @property {'outline' | 'filled'} [variant]
 */

/**
 * Select component with enhanced functionality including search, lazy loading, and custom options
 * @param {SelectProps} props - The component props
 * @returns {JSX.Element} The Select component
 */

const Select = ({
  AddItemButtonText = 'Add to list',
  AddItemInternalValidationErrorText = 'Item is not valid',
  renderNewItemLabel,
  addItemError = '',
  addItemLoader = false,
  addItemValidation,
  allowClear,
  backspaceTagRemove = true,
  className,
  customOptions,
  disabled = false,
  dropdownClassName,
  error,
  style = {},
  showSearch = true,
  placeholder,
  onChange = () => {},
  options = [],
  value,
  variant = VARIANTS.OUTLINE,
  getPopupContainer,
  id,
  labelText,
  popup,
  multiple = false,
  readOnly = false,
  labelAsValue = false,
  optionFilterProp = 'value',
  optionLabelProp = 'children',
  filterOption,
  notification,
  name,
  onSearch,
  onAddItem,
  onLazyLoad,
  lazyLoader = false,
  searchLoader = false,
  searchValue,
  onClear,
  onBlur,
  tooltipText,
  isOpen,
  tagsRenderStyle = 'single',
  tagMaxWidth,
  onSelect,
  width,
  virtual = false,
  onDeselect,
  selectionLimit,
  onDropdownVisibleChange,
  tooltipChildrenWrapperStyles = {},
  maxTagCount,
  meta = {},
  listHeight,
  ariaLabel,
}) => {
  const wrapperRef = useRef()
  const addItemButtonRef = useRef()
  const addItemErrorRef = useRef()
  const { Option } = AntdSelect
  const [internalValidationError, setInternalValidationError] = useState(true)
  const [currentSearch, setCurrentSearch] = useState(searchValue || '')
  const [lastItemAdded, setLastItemAdded] = useState(null)
  const isControlledOpen = isOpen !== undefined
  const [open, setOpen] = useState(isOpen ?? false)

  useEffect(() => {
    if (searchValue !== undefined && searchValue !== currentSearch) {
      setCurrentSearch(searchValue)
    }
  }, [searchValue])

  useEffect(() => {
    if (isControlledOpen) {
      setOpen(!!isOpen)
    }
  }, [isControlledOpen, isOpen])

  useEffect(() => {
    // part of the bug with responsive maxTagCount
    if (wrapperRef.current && multiple && tagsRenderStyle === 'multiple') {
      const searchInput = wrapperRef.current.querySelector('input')

      if (searchInput) {
        searchInput.blur()
      }
    }
  }, [])

  const optionsWithExtractedLabels = useMemo(
    () =>
      options.map((opt) => ({
        option: opt,
        extractedLabel: extractReactNodeText(opt?.label),
      })),
    [options]
  )

  const onScrollCheck = ({ target }) => {
    const doubleClientHeight = target.clientHeight * 2
    const maxScroll = target.scrollHeight - doubleClientHeight

    if (target.scrollTop >= maxScroll && !lazyLoader) {
      onLazyLoad()
    }
  }

  const handleOnSearch = (search) => {
    setCurrentSearch(search)

    if (onSearch) {
      onSearch(search)
    }
  }

  const handleAddItem = () => {
    if (onAddItem) {
      setLastItemAdded(currentSearch)

      const isNotValid = !!addItemValidation && !addItemValidation(currentSearch)
      setInternalValidationError(isNotValid)

      if (!isNotValid) {
        onAddItem(currentSearch)
        setCurrentSearch('')

        // part of the bug with responsive maxTagCount
        if (multiple && tagsRenderStyle === 'multiple') {
          const searchInput = wrapperRef.current.querySelector('input')

          if (searchInput) {
            searchInput.blur()
          }
        }
      }
    }
  }

  // close search panel after new item successfully added to valid options
  useEffect(() => {
    if (
      lastItemAdded &&
      optionsWithExtractedLabels.some(({ extractedLabel }) => extractedLabel === lastItemAdded)
    ) {
      setOpen(false)
      setLastItemAdded(null)
    }
  }, [optionsWithExtractedLabels, lastItemAdded])

  // Check if there's an exact match for current search in options
  const hasExactMatch =
    currentSearch.trim() &&
    optionsWithExtractedLabels.some(
      ({ option: opt, extractedLabel }) =>
        extractedLabel?.toLowerCase() === currentSearch.trim().toLowerCase() ||
        opt.value?.toString().toLowerCase() === currentSearch.trim().toLowerCase()
    )

  // Check if there are any matching options that will be displayed in dropdown
  // This uses the same filter logic as Ant Design's default (contains search, case-insensitive)
  const hasMatchingOptions =
    currentSearch.trim() &&
    optionsWithExtractedLabels.some(({ option: opt, extractedLabel }) => {
      const searchLower = currentSearch.trim().toLowerCase()
      const labelMatch = extractedLabel?.toLowerCase()?.includes(searchLower)
      const valueMatch = opt.value?.toString().toLowerCase().includes(searchLower)
      return optionFilterProp === 'label' ? labelMatch : labelMatch || valueMatch
    })

  // Component for "Add to list" button
  const canShowAddItem = onAddItem && currentSearch.trim() && !hasExactMatch
  const showAddItemError =
    canShowAddItem && (internalValidationError || addItemError) && currentSearch === lastItemAdded

  let addItemButtonComponent = null
  if (showAddItemError) {
    addItemButtonComponent = (
      <div className="add-item-error" ref={addItemErrorRef}>
        {internalValidationError ? AddItemInternalValidationErrorText : addItemError}
      </div>
    )
  } else if (canShowAddItem && addItemLoader) {
    addItemButtonComponent = <Loading />
  } else if (canShowAddItem) {
    addItemButtonComponent = (
      <div ref={addItemButtonRef}>
        <Button
          text={
            renderNewItemLabel && typeof renderNewItemLabel === 'function'
              ? renderNewItemLabel(currentSearch)
              : AddItemButtonText
          }
          styleType="tertiary"
          onClick={handleAddItem}
          className="add-item-button"
          id="Dropdown_AddItem_Button"
        />
      </div>
    )
  }

  // Show button in notFoundContent only when there are NO matching options
  let notFoundContentComponent = null
  if (searchLoader) {
    notFoundContentComponent = <Loading />
  } else if (!hasMatchingOptions) {
    notFoundContentComponent = addItemButtonComponent
  }

  const dropdownRenderFn = useCallback(
    (defaultDropdown) => (
      <SelectDropdownRender
        defaultDropdown={defaultDropdown}
        searchLoader={searchLoader}
        hasMatchingOptions={hasMatchingOptions}
        addItemButtonComponent={addItemButtonComponent}
      />
    ),
    [searchLoader, hasMatchingOptions, addItemButtonComponent]
  )

  const currentValue =
    value === '' ? undefined : labelAsValue ? { value: value?.label, label: value?.label } : value

  const handleChange = (updatedValue, option) => {
    if (selectionLimit && Array.isArray(updatedValue) && updatedValue?.length > selectionLimit) {
      return
    }
    onChange(updatedValue, option)
  }

  const handleSelect = (val, option) => {
    if (onSelect) {
      onSelect(val, option)
    }

    const optionLabelText = extractReactNodeText(option?.label)
    const searchableLabel = optionLabelText || option?.value

    if (searchableLabel?.toLowerCase().includes(currentSearch.toLowerCase())) {
      setCurrentSearch('')
    }
    /*
        This code is to catch specific cases where user clicks twice on same option
        Since normally selectors don't trigger onChange handler for same value, here we do it
        manually to catch clicking twice on same field like "custom range date"
      */
    if (val === currentValue?.value) {
      handleChange(val, option)
    }
  }

  const DropdownComponent = (
    <div id={id ? `${id}-container` : undefined} style={{ width: '100%' }}>
      <AntdSelect
        id={id}
        notFoundContent={notFoundContentComponent}
        onPopupScroll={(e) => {
          if (typeof onLazyLoad === 'function') {
            onScrollCheck(e)
          }
        }}
        onOpenChange={(visible) => {
          setOpen(visible)

          if (!visible) {
            setCurrentSearch('')
          }

          // Call the prop handler if provided
          if (onDropdownVisibleChange) {
            onDropdownVisibleChange(visible)
          }
        }}
        onInputKeyDown={(event) => {
          if (event.key === 'Enter' && (addItemButtonRef.current || addItemErrorRef.current)) {
            event.stopPropagation()

            if (addItemButtonRef.current) {
              handleAddItem()
            }
          }
          // prevent deleting tags on multiple select
          if (!backspaceTagRemove && event.key === 'Backspace') {
            event.stopPropagation()
          }
        }}
        allowClear={
          allowClear || multiple
            ? {
                clearIcon: (
                  <ClearIcon
                    width="16px"
                    height="16px"
                    bgColor={variant === VARIANTS.FILLED ? 'var(--ds-blue-6)' : null}
                    bgHoverColor={variant === VARIANTS.FILLED ? 'var(--ds-blue-5)' : null}
                  />
                ),
              }
            : false
        }
        status={
          error ||
          (onAddItem &&
            (internalValidationError || addItemError) &&
            currentSearch === lastItemAdded)
            ? 'error'
            : ''
        }
        mode={multiple ? 'multiple' : undefined}
        classNames={{
          popup: {
            root: cls(
              DS_SELECT_POPUP_CLASS,
              { [DS_SELECT_POPUP_MULTIPLE_CLASS]: multiple },
              dropdownClassName
            ),
          },
        }}
        style={style}
        showSearch={
          !(selectionLimit && Array.isArray(value) && value?.length >= selectionLimit) &&
          (showSearch || !!onAddItem)
        }
        placeholder={placeholder}
        aria-label={ariaLabel}
        suffixIcon={<ChevronDown width="16px" height="16px" fill="var(--ds-gray-2)" />}
        onChange={handleChange}
        onSelect={handleSelect}
        onSearch={handleOnSearch}
        value={currentValue}
        {...(isControlledOpen ? { open } : {})}
        className={cls('is-antd-select', className)}
        meta={meta}
        virtual={virtual}
        disabled={disabled}
        getPopupContainer={getPopupContainer || (() => wrapperRef.current)}
        tagRender={CustomTag({ tagMaxWidth, tagsRenderStyle })}
        maxTagCount={
          maxTagCount ||
          (multiple && tagsRenderStyle === 'multiple' && !currentSearch ? 'responsive' : 0)
        }
        maxTagPlaceholder={CustomMaxTagPlaceholder({ tagsRenderStyle, showShort: !!currentSearch })}
        // there is an antd bug with responsive maxTagCount without focus: https://github.com/ant-design/ant-design/issues/29444
        autoFocus={multiple && tagsRenderStyle === 'multiple' ? true : undefined}
        popupMatchSelectWidth={false}
        styles={{
          popup: {
            root: {
              maxWidth: 420,
              textAlign: 'left',
              width: width || 'auto',
              zIndex: 10000,
            },
          },
        }}
        optionFilterProp={optionFilterProp}
        optionLabelProp={optionLabelProp}
        filterOption={filterOption}
        name={name}
        searchValue={currentSearch}
        onClear={onClear}
        onBlur={onBlur}
        onDeselect={onDeselect}
        popupRender={dropdownRenderFn}
        listHeight={listHeight}
      >
        <>
          {customOptions
            ? customOptions.map((el) => (
                <el key={el.key} {...el.props}>
                  {el.props.children}
                </el>
              ))
            : options.map((option) => {
                const optionKey = option.key || `${id}_${option.value}`
                return (
                  <Option
                    key={option.key || option.value}
                    label={option.label}
                    value={labelAsValue ? option.label : option.value}
                    data={option}
                    className={option.className}
                  >
                    <SelectOptionContent
                      option={option}
                      multiple={multiple}
                      value={value}
                      labelAsValue={labelAsValue}
                      optionKey={optionKey}
                    />
                  </Option>
                )
              })}
          {lazyLoader && (
            <Option key="lazyLoader">
              <Loading className="lazy-loader" />
            </Option>
          )}
        </>
      </AntdSelect>
    </div>
  )

  return (
    <ConfigProvider
      // properties of token can not be undefined while styl color not immediately loaded
      theme={{
        token: 'var(--primary)'
          ? {
              colorPrimary: 'var(--primary)',
              fontFamily: 'var(--t-typography-font-family-font-family-default)',
              colorBgElevated: 'var(--color-elevated-solid)',
              colorText: 'var(--ds-gray-1)',
            }
          : undefined,
      }}
    >
      <DesignSystemSelectPopupGlobalStyle />
      <SelectStyleWrapper
        disabled={disabled}
        width={width}
        multiple={multiple}
        className="select-dropdown"
        variant={variant}
      >
        <SelectWrapper
          id={`select-wrapper-${id}`}
          ref={wrapperRef}
          width={width}
          disabled={disabled}
          variant={variant}
          className={cls(className, 'typography-subtitle-4', {
            open,
            readonly: readOnly,
            error: meta?.error && meta?.touched,
          })}
        >
          {labelText && (
            <LabelText
              text={labelText}
              disabled={disabled}
              notification={notification}
              tooltipText={tooltipText}
              id={id}
            />
          )}
          {popup ? (
            <Tooltip
              overlayStyle={{
                maxWidth: '250px',
                whiteSpace: 'normal',
              }}
              childrenWrapperStyle={{ display: 'flex', ...tooltipChildrenWrapperStyles }}
              text={popup.text}
            >
              {DropdownComponent}
            </Tooltip>
          ) : (
            DropdownComponent
          )}
        </SelectWrapper>
      </SelectStyleWrapper>
    </ConfigProvider>
  )
}

export default Select

Select.Field = withValidation(Select)

const SelectWrapper = styled.div`
  width: ${({ width }) => (width ? `${width}px` : '100%')};

  // loading
  .main-loader {
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;

    &.lazy-loader {
      height: 18px;
      justify-content: flex-start;
    }

    &__text {
      color: var(--ds-gray-2);
      font-size: 14px;
      margin-left: 8px;
    }
  }

  .ant-select-item-empty {
    padding: 0px !important;
    .main-loader {
      height: 64px;
    }
  }

  .ant-select-selector {
    background: ${({ variant }) =>
      variant === VARIANTS.FILLED ? 'var(--ds-blue-8)' : 'var(--color-surface)'} !important;
  }

  &:not(.error) .ant-select:not(.ant-select-disabled):hover .ant-select-selector,
  &:not(.error) .ant-select-open:not(.ant-select-disabled) .ant-select-selector {
    border-color: var(--primary) !important;
  }

  // width
  .ant-select {
    width: 100%;
    max-width: unset;
  }

  // readonly
  &.readonly {
    pointer-events: none;

    .ant-select-arrow {
      display: none;
    }
  }

  // selected tags
  .ant-select-selection-overflow-item.ant-select-selection-overflow-item-rest {
    .ant-select-selection-item {
      margin: 0;
      padding: 0 !important;
      background: none;
      margin-inline-end: 6px;

      .ant-select-selection-item-content {
        margin-inline-end: 0;
      }
    }
  }

  .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    /* font-weight: var(--regular); */
  }

  // disabled
  .ant-select-disabled.ant-select:not(.ant-select-customize-input) {
    .ant-select-selector {
      color: var(--ds-gray-5);
      border: 1px solid var(--ds-gray-7);
      background: ${({ variant }) =>
        variant === VARIANTS.FILLED ? 'var(--ds-gray-8)' : 'var(--color-surface)'};
      cursor: default;
    }

    .ant-select-arrow {
      svg {
        path {
          fill: var(--ds-gray-5);
        }
      }
    }
  }

  // error
  &.error .ant-select-selector {
    border-color: var(--negative-color) !important;
  }

  // search-input
  .ant-select-selection-search {
    margin: 0;
  }

  // state
  .ant-select-dropdown .ant-select-item-option-state {
    display: none;
  }

  // select-option
  .ant-select-selection-item {
    color: var(--ds-gray-1) !important;
    .option {
      .option_label {
        display: block;
        white-space: nowrap;
      }
    }
  }

  .option {
    height: 100%;
    display: flex;
    gap: 12px;
    align-items: center;

    .option_label {
      min-width: 0;
      min-width: 0;
      display: -webkit-box;
      overflow: hidden;
      line-height: normal;
      text-overflow: ellipsis;
      white-space: normal;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .left_icon {
      display: flex;
      align-items: center;
    }
  }

  // add-item-error
  .add-item-error {
    color: var(--negative-color);
    font-weight: 600;
    font-size: 12px;
    width: 100%;
    padding: 8px 12px;
    display: flex;
    justify-content: left;
  }

  // add-item-button
  .add-item-button {
    font-weight: 600;
    font-size: 12px;
    justify-content: left;
  }

  .pre-label {
    color: var(--sub-text-color);
    font-size: var(--caption-text);
    width: 38px;
    margin-right: 8px;
    padding: 2px 8px 4px;
    border-radius: var(--border-radius);
    background-color: var(--dropdown-pre-label);
    line-height: 1;
    text-align: center;
    &.icon {
      padding: 1.5px 11px;
    }
  }

  .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
    background-color: var(--dropdown-hover);
    .pre-label {
      background-color: var(--ds-gray-8);
    }
  }
  .ant-select-multiple .ant-select-selection-wrap {
    align-self: revert !important;
  }

  // scrollbar
  * {
    ${styledDropdownScrollbar()}
  }
`
